import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { FulfillmentType, Selections } from '@pizza-roma/shared';

export interface CartLine {
  key: string;
  menuItemId: string;
  name: string;
  imageUrl: string | null;
  quantity: number;
  selections: Selections;
  optionsLabel: string;
  /** Option names as stored in the menu (translated at display). */
  optionNames?: string[];
  unitPriceCents: number;
}

interface CartState {
  slug: string;
  lines: CartLine[];
  mode: FulfillmentType;
  setMode: (m: FulfillmentType) => void;
  add: (l: Omit<CartLine, 'key'>) => void;
  setQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  clear: () => void;
  count: number;
  subtotalCents: number;
}

const Ctx = createContext<CartState | null>(null);
const storageKey = (slug: string) => `pr-cart:${slug}`;

function load(slug: string): { lines: CartLine[]; mode: FulfillmentType } {
  try {
    const raw = localStorage.getItem(storageKey(slug));
    if (raw) {
      const v = JSON.parse(raw);
      if (Array.isArray(v.lines)) return { lines: v.lines, mode: v.mode === 'PICKUP' ? 'PICKUP' : 'DELIVERY' };
    }
  } catch {
    /* storage unavailable */
  }
  return { lines: [], mode: 'DELIVERY' };
}

/** Cart persisted per restaurant in localStorage (no private data: just items and options). */
export function CartProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [state, setState] = useState(() => load(slug));
  useEffect(() => setState(load(slug)), [slug]);
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(slug), JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [slug, state]);

  const value = useMemo<CartState>(() => {
    const keyOf = (l: { menuItemId: string; selections: Selections }) =>
      `${l.menuItemId}|${Object.keys(l.selections)
        .sort()
        .map((k) => `${k}:${[...l.selections[k]].sort().join(',')}`)
        .join(';')}`;
    return {
      slug,
      lines: state.lines,
      mode: state.mode,
      setMode: (mode) => setState((s) => ({ ...s, mode })),
      add: (l) =>
        setState((s) => {
          const key = keyOf(l);
          const existing = s.lines.find((x) => x.key === key);
          const lines = existing
            ? s.lines.map((x) => (x.key === key ? { ...x, quantity: Math.min(50, x.quantity + l.quantity) } : x))
            : [...s.lines, { ...l, key }];
          return { ...s, lines };
        }),
      setQty: (key, qty) =>
        setState((s) => ({ ...s, lines: qty <= 0 ? s.lines.filter((x) => x.key !== key) : s.lines.map((x) => (x.key === key ? { ...x, quantity: Math.min(50, qty) } : x)) })),
      remove: (key) => setState((s) => ({ ...s, lines: s.lines.filter((x) => x.key !== key) })),
      clear: () => setState((s) => ({ ...s, lines: [] })),
      count: state.lines.reduce((n, l) => n + l.quantity, 0),
      subtotalCents: state.lines.reduce((n, l) => n + l.unitPriceCents * l.quantity, 0),
    };
  }, [slug, state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  const c = useContext(Ctx);
  if (!c) throw new Error('CartProvider missing');
  return c;
}

/** Orders placed from this browser (guest tracking links). Tokens only, no personal data. */
export function rememberOrder(slug: string, o: { publicToken: string; number?: number; createdAt: string }) {
  try {
    const k = `pr-orders:${slug}`;
    const list = JSON.parse(localStorage.getItem(k) ?? '[]') as unknown[];
    localStorage.setItem(k, JSON.stringify([o, ...list].slice(0, 20)));
  } catch {
    /* ignore */
  }
}

export function rememberedOrders(slug: string): { publicToken: string; number?: number; createdAt: string }[] {
  try {
    return JSON.parse(localStorage.getItem(`pr-orders:${slug}`) ?? '[]');
  } catch {
    return [];
  }
}
